import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getPlayerStats } from '../services/fortniteApi';
import StatCard from '../components/StatCard';
import { 
  Trophy, Swords, Zap, Skull, TrendingUp, Clock, Star, Target, 
  User, Users, Users2, Globe, Calendar 
} from 'lucide-react';

const Stats = () => {
  const { username } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeWindow, setTimeWindow] = useState('lifetime'); // 'lifetime' or 'season'
  const [activeTab, setActiveTab] = useState('overall'); // 'overall', 'solo', 'duo', 'squad'

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await getPlayerStats(username, timeWindow);
        setData(result.data);
      } catch (err) {
        setError(err.response?.data?.error || 'Joueur non trouvé');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [username, timeWindow]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-16 h-16 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin"></div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex flex-col items-center justify-center px-8 text-center">
      <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mb-6">
        <Skull className="w-10 h-10 text-red-500" />
      </div>
      <h2 className="text-4xl mb-4 font-black">JOUEUR INTROUVABLE</h2>
      <p className="text-white/50 mb-8 max-w-md">{error}</p>
      <button onClick={() => window.history.back()} className="glass-card px-8 py-3 hover:bg-white/5 transition-all">Retour à l'accueil</button>
    </div>
  );

  const statsSource = data.stats.all;
  const currentStats = statsSource[activeTab];

  if (!currentStats) {
    return (
      <div className="pt-[calc(var(--nav-height)+2rem)] pb-20 px-8 max-w-7xl mx-auto text-center">
        <p className="text-white/50">Aucune statistique disponible pour ce mode de jeu.</p>
        <button onClick={() => setActiveTab('overall')} className="mt-4 text-purple-500 font-bold">Voir Global</button>
      </div>
    );
  }

  const tabs = [
    { id: 'overall', label: 'Global', icon: Globe },
    { id: 'solo', label: 'Solo', icon: User },
    { id: 'duo', label: 'Duo', icon: Users },
    { id: 'squad', label: 'Section', icon: Users2 },
  ];

  return (
    <div className="pt-[calc(var(--nav-height)+2rem)] pb-20 px-8 max-w-7xl mx-auto">
      <header className="mb-12 flex flex-col lg:flex-row lg:items-end gap-8 justify-between">
        <div className="flex-1">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-4 mb-3"
          >
            <div className="px-3 py-1 bg-purple-600 rounded-full text-[10px] font-black uppercase tracking-widest text-white shadow-lg shadow-purple-600/20">
              Profil Joueur
            </div>
            <span className="text-white/30 font-bold tracking-widest text-xs uppercase flex items-center gap-2">
              <Star className="w-3 h-3 text-fortnite-gold fill-fortnite-gold" />
              Niveau {data.battlePass?.level || '??'}
            </span>
          </motion.div>
          <motion.h1 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-5xl md:text-8xl font-black uppercase leading-none mb-4"
          >
            {data.account.name}
          </motion.h1>
        </div>

        {/* Time Window Toggle */}
        <div className="glass-card p-1.5 flex gap-1 self-start lg:self-end">
          <button 
            onClick={() => setTimeWindow('lifetime')}
            className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center gap-2 ${timeWindow === 'lifetime' ? 'bg-purple-600 text-white shadow-lg' : 'text-white/40 hover:text-white'}`}
          >
            <Globe className="w-4 h-4" /> Global
          </button>
          <button 
            onClick={() => setTimeWindow('season')}
            className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center gap-2 ${timeWindow === 'season' ? 'bg-purple-600 text-white shadow-lg' : 'text-white/40 hover:text-white'}`}
          >
            <Calendar className="w-4 h-4" /> Saison
          </button>
        </div>
      </header>

      {/* Mode Tabs */}
      <div className="flex flex-wrap gap-2 mb-10 overflow-x-auto pb-4 scrollbar-hide">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-8 py-4 rounded-2xl font-black uppercase tracking-widest text-xs transition-all border ${
              activeTab === tab.id 
              ? 'bg-white/10 border-white/20 text-white shadow-xl' 
              : 'bg-transparent border-transparent text-white/30 hover:text-white/60'
            }`}
          >
            <tab.icon className={`w-4 h-4 ${activeTab === tab.id ? 'text-purple-500' : ''}`} />
            {tab.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={`${activeTab}-${timeWindow}`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <StatCard 
              label="Victoires Royale" 
              value={currentStats.wins} 
              icon={Trophy} 
              colorClass="from-yellow-600/30 to-yellow-500/5"
              subtext={`${currentStats.winRate?.toFixed(1) || 0}% de victoire`}
            />
            <StatCard 
              label="Kills Totaux" 
              value={currentStats.kills} 
              icon={Swords} 
              colorClass="from-red-600/30 to-red-500/5"
              subtext={`${currentStats.kd?.toFixed(2) || 0} ratio K/D`}
            />
            <StatCard 
              label="Parties Jouées" 
              value={currentStats.matches} 
              icon={Zap} 
              colorClass="from-blue-600/30 to-blue-500/5"
              subtext="Total des matchs"
            />
            <StatCard 
              label="Morts" 
              value={currentStats.deaths} 
              icon={Skull} 
              colorClass="from-purple-600/30 to-purple-500/5"
              subtext="Matchs perdus"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 glass-card p-10 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-8 opacity-[0.03] pointer-events-none">
                <TrendingUp className="w-64 h-64" />
              </div>
              <h3 className="text-3xl font-black mb-10 flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                   <TrendingUp className="text-purple-500 w-6 h-6" />
                </div>
                Performances Détaillées
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-12">
                <div className="space-y-8">
                  <div className="flex justify-between items-center group border-b border-white/5 pb-6">
                    <span className="text-white/40 group-hover:text-white/60 transition-colors flex items-center gap-3">
                      <Clock className="w-5 h-5 text-purple-400" /> Temps de jeu
                    </span>
                    <span className="text-xl font-bold">{(currentStats.minutesPlayed / 60).toFixed(0)} Heures</span>
                  </div>
                  <div className="flex justify-between items-center group border-b border-white/5 pb-6">
                    <span className="text-white/40 group-hover:text-white/60 transition-colors flex items-center gap-3">
                      <Target className="w-5 h-5 text-red-400" /> Kills / Match
                    </span>
                    <span className="text-xl font-bold">{currentStats.killsPerMatch?.toFixed(2) || 0}</span>
                  </div>
                </div>
                <div className="space-y-8">
                  <div className="flex justify-between items-center group border-b border-white/5 pb-6">
                    <span className="text-white/40 group-hover:text-white/60 transition-colors flex items-center gap-3">
                      <Zap className="w-5 h-5 text-blue-400" /> Score / Match
                    </span>
                    <span className="text-xl font-bold">{currentStats.scorePerMatch?.toFixed(0) || 0}</span>
                  </div>
                  <div className="flex justify-between items-center group border-b border-white/5 pb-6">
                    <span className="text-white/40 group-hover:text-white/60 transition-colors flex items-center gap-3">
                      <Star className="w-5 h-5 text-yellow-400" /> Score / Min
                    </span>
                    <span className="text-xl font-bold">{currentStats.scorePerMin?.toFixed(2) || 0}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="glass-card p-10 bg-gradient-to-br from-purple-900/10 via-transparent to-transparent">
              <h3 className="text-2xl font-black mb-10 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-fortnite-gold/20 flex items-center justify-center">
                   <Star className="text-fortnite-gold w-5 h-5" />
                </div>
                Passe de Combat
              </h3>
              <div className="flex flex-col items-center py-4">
                <div className="relative w-48 h-48 mb-8">
                   <svg className="w-full h-full transform -rotate-90">
                    <circle cx="96" cy="96" r="90" stroke="currentColor" strokeWidth="12" fill="transparent" className="text-white/5"/>
                    <motion.circle 
                      cx="96" cy="96" r="90" stroke="currentColor" strokeWidth="12" fill="transparent" 
                      strokeDasharray={2 * Math.PI * 90} 
                      initial={{ strokeDashoffset: 2 * Math.PI * 90 }}
                      animate={{ strokeDashoffset: 2 * Math.PI * 90 * (1 - (data.battlePass?.progress / 100)) }}
                      transition={{ duration: 1.5, ease: "easeOut" }}
                      className="text-purple-500"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <motion.span 
                      initial={{ scale: 0.5, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className="text-5xl font-black"
                    >
                      {data.battlePass?.level}
                    </motion.span>
                    <span className="text-xs uppercase font-black text-white/40 tracking-widest bg-white/5 px-3 py-1 rounded-full mt-2">Niveau</span>
                  </div>
                </div>
                <div className="w-full text-center">
                   <p className="text-sm text-white/40 font-bold uppercase tracking-widest mb-2">{data.battlePass?.progress}% Terminé</p>
                   <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
                      <motion.div 
                        initial={{ width: 0 }}
                        animate={{ width: `${data.battlePass?.progress}%` }}
                        transition={{ duration: 1, delay: 0.5 }}
                        className="h-full bg-purple-500 shadow-[0_0_10px_rgba(168,85,247,0.5)]"
                      />
                   </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default Stats;
